import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { Video } from '../../../API/types/Videos';
import Icon from '../../../components/common/Icon/Icon';
import { IconKeys } from '../../../components/common/Icon/IconKeys';
import Tags from '../../../components/common/Tags';
import ImageWithFallback from '../../../components/common/ImageWithFallback/ImageWithFallback';
import S from './VideoItemStyles';
import { getFormattedDateAndTime, formatDurationFromRange } from '../../../utils/dateUtils';
import { getFulfillmentDetailLines, getFulfillmentStatusLabel } from '../../../utils/videoFulfillmentUtils';
import { FeaturesManagement } from '../../../featuresControl';
import CircularProgress from '../../../components/common/CircularProgress/CircularProgress';
import useGetAlertTypeById from '../../../API/hooks/alerts/useGetAlertTypeById';
import { alertEventsMap } from '../../Alerts/constants/alertEventsMap';
import { alertSensorMap } from '../../Alerts/constants/alertSensorMap';
import { ThemeType } from '../../../theme/themeType';

const uploadProgressCache = new Map<string, number>();

const getWrapperStyles = (status: string, isSelecting: boolean, isSelected: boolean): string => {
  if (status !== 'completed') {
    if (isSelecting) return 'disabled';
    return '';
  }
  return `${isSelecting ? ' selecting' : ''}${isSelected ? ' checked' : ''}`;
};

type Props = {
  item?: Video;
  isSelecting?: boolean;
  isSelected?: boolean;
  onSelect?: (checked: boolean, id: string) => void;
};

type VideoStatusType = 'pending' | 'startedProcessing' | 'failedToRequest' | 'failedToUpload' | 'completed' | 'other' | 'failedFinally' | 'failedAttemptsLimitExceeded' | 'failedDeviceRemovedFromFleet';

const VIDEO_STATUS = {
  other: 'No video',
  pending: 'Requesting video...',
  startedProcessing: 'Uploading',
  failedToRequest: 'confirming video availability',
  failedToUpload: 'confirming video availability',
  completed: 'Video present',
  failedFinally: 'Video is not available on the device',
  failedAttemptsLimitExceeded: 'video could not be downloaded due to connection issue',
  failedDeviceRemovedFromFleet: 'Video unavailable - device was removed from the fleet',
};

function VideoItem({
  item, isSelected, isSelecting, onSelect,
}: Props): JSX.Element {
  const navigate = useNavigate();
  const theme = useTheme() as ThemeType;

  const {
    _id: id,
    fromTimestamp,
    toTimestamp,
    alertTimestamp,
    vehicle,
    thumbnailUrls,
    status,
    alertId,
    fleetId,
    uploadPercentage,
    fulfillmentStatus,
    timeSynchronized,
  } = item!;
  const { data: alert } = useGetAlertTypeById(alertId, fleetId);
  const alertEvent = alert?.name ? alertEventsMap[alert.name] : undefined;
  const alertSensorLabel = (alert?.sensor && alertSensorMap[alert.sensor]) || 'Alert';
  const thumbnail = thumbnailUrls[0];
  const alertDate = fromTimestamp || alertTimestamp;
  const duration = formatDurationFromRange(fromTimestamp, toTimestamp);
  const wrapperStyles = getWrapperStyles(status, isSelecting!, isSelected!);

  if (typeof uploadPercentage === 'number') {
    uploadProgressCache.set(id, uploadPercentage);
  } else if (status === 'completed') {
    uploadProgressCache.delete(id);
  }
  const cachedPercentage = uploadProgressCache.get(id) ?? null;
  const stableUploadPercentage = typeof uploadPercentage === 'number' ? uploadPercentage : cachedPercentage;

  const fulfillmentDetailLines = getFulfillmentDetailLines(item!);
  const statusLabel = getFulfillmentStatusLabel(
    fulfillmentStatus,
    VIDEO_STATUS[item?.status as VideoStatusType || 'other'],
    status,
  );
  const fulfillmentTooltip = fulfillmentDetailLines.join(' ');
  const showTimeSyncStatus = timeSynchronized !== null || status === 'completed';
  const isSynchronized = timeSynchronized !== false;
  let timeSyncClassName = '';
  if (alertId && alertEvent && vehicle?.licensePlateNumber) {
    timeSyncClassName = 'with-alert-type';
  } else if ((alertId && alertEvent) || vehicle?.licensePlateNumber) {
    timeSyncClassName = 'with-model';
  }

  const handleSelect = useCallback((): void => {
    if (isSelecting) {
      if (status === 'completed' && onSelect) {
        onSelect!(isSelected as boolean, id);
      }
      return;
    }

    navigate(`/videos/${id}`);
  }, [id, isSelecting, isSelected]);
  return (
    <S.Wrapper className={`${wrapperStyles}${fulfillmentDetailLines.length ? ' has-fulfillment' : ''}`}>
      <button type="button" className="video-item" onClick={handleSelect}>
        {!thumbnail && (
          <div className="no-video">
            {stableUploadPercentage != null ? (
              <CircularProgress
                progress={stableUploadPercentage}
                initialProgress={stableUploadPercentage}
                showLabel={false}
              />
            ) : (
              <Icon iconKey={IconKeys.EYE_OFF} />
            )}
          </div>
        )}
        {thumbnail && (
          <div className="thumbnail">
            <ImageWithFallback
              src={FeaturesManagement.productivity.thumbnails ? thumbnail : undefined}
              alt="thumbnail"
            />
          </div>
        )}
        <div className="video-info" title={alertId ? 'Alert video' : 'Custom VoD'}>
          {vehicle?.licensePlateNumber && (
            <div className="model">
              {alertId && (
                <Tags.LabeledIcon icon={IconKeys.SPARK_ALERT} label={vehicle?.licensePlateNumber} />
              )}
              {!alertId && (
                <Tags.LabeledIcon icon={IconKeys.VIDEO} label={vehicle?.licensePlateNumber} />
              )}
            </div>
          )}
          {alertId && alertEvent && (
            <div
              className={`alert-type${vehicle?.licensePlateNumber ? '' : ' no-plate'}`}
              title={alertEvent.fullDesc}
            >
              <Tags.Toggler type={alertSensorLabel} trigger={alertEvent.badge} />
            </div>
          )}
          {showTimeSyncStatus && (
            <div className={`time-sync${timeSyncClassName ? ` ${timeSyncClassName}` : ''}`}>
              <Tags.Status
                label={isSynchronized ? 'Synchronized' : 'Not Synchronized'}
                backgroundColor={isSynchronized ? theme.colors.green : theme.colors.red}
              />
            </div>
          )}
          <div className="map">
            <Tags.Icon
              className="map-icon"
              icon={IconKeys.LOCATION}
              background="#292929"
            />
          </div>
          <div className={`video-meta${fulfillmentDetailLines.length ? ' with-fulfillment' : ''}`}>
            {duration && <div className="time">{`Duration: ${duration}`}</div>}
            <div className="date">
              {getFormattedDateAndTime(alertDate ? new Date(alertDate) : '').replace(',', '')}
            </div>
          </div>
          <div className={`video-footer${fulfillmentDetailLines.length ? ' with-fulfillment' : ''}`}>
            <div
              className="video-status"
              title={fulfillmentTooltip || statusLabel}
            >
              {statusLabel}
            </div>
            {fulfillmentDetailLines.map((line) => (
              <div className="video-fulfillment-note" key={line} title={line}>
                {line}
              </div>
            ))}
          </div>
        </div>
      </button>
    </S.Wrapper>
  );
}

export default VideoItem;
